/**
 * Collapses a detailed race-plan segment list (one row per climb/descent/rolling
 * boundary, plus checkpoint stop rows) into a "minimal" list: one aggregated row
 * per checkpoint-to-checkpoint gap, with the checkpoint stop rows passed through
 * unchanged.
 */

// A segment counts as a pure climb/descend only when the dominant direction
// outweighs the other by at least this ratio (e.g. +500/-100 -> climb,
// +500/-200 -> mixed).
const _DOMINANCE_RATIO = 4;

function classifyByElevation(gainM, lossM) {
  const gain = gainM || 0;
  const loss = lossM || 0;
  if (gain + loss < 20) return { type: 'rolling', detail: null };
  if (gain > loss * _DOMINANCE_RATIO) return { type: 'climb', detail: null };
  if (loss > gain * _DOMINANCE_RATIO) return { type: 'descend', detail: null };
  return { type: 'mixed', detail: null };
}

function mergeRouteSegments(bucket) {
  if (bucket.length === 1) {
    return { ...bucket[0] };
  }

  const first = bucket[0];
  const last = bucket[bucket.length - 1];

  let elevationGainM = 0;
  let elevationLossM = 0;
  let paceNum = 0, paceDen = 0;
  let gapNum = 0, gapDen = 0;
  let ascNum = 0, ascDen = 0;
  let descNum = 0, descDen = 0;

  for (const seg of bucket) {
    elevationGainM += seg.elevation_gain_m || 0;
    elevationLossM += seg.elevation_loss_m || 0;

    const lenKm = (seg.length_m || 0) / 1000;
    if (seg.moving_pace != null && lenKm > 0) {
      paceNum += seg.moving_pace * lenKm;
      paceDen += lenKm;
    }
    if (seg.moving_gap != null && lenKm > 0) {
      gapNum += seg.moving_gap * lenKm;
      gapDen += lenKm;
    }

    const durS = seg.duration_s || 0;
    if (seg.moving_asc_vertical_speed != null && durS > 0) {
      ascNum += seg.moving_asc_vertical_speed * durS;
      ascDen += durS;
    }
    if (seg.moving_desc_vertical_speed != null && durS > 0) {
      descNum += seg.moving_desc_vertical_speed * durS;
      descDen += durS;
    }
  }

  const { type, detail } = classifyByElevation(elevationGainM, elevationLossM);

  const elapsedStartS = first.elapsed_start_s;
  const elapsedS = last.elapsed_s;
  const durationS =
    elapsedS != null && elapsedStartS != null ? elapsedS - elapsedStartS : null;

  return {
    index: 0, // reindexed by caller
    start_label: first.start_label,
    end_label: last.end_label,
    // A merged leg that ends at a user-placed checkpoint still ends at it —
    // without this the grid loses its edit/remove affordance in Minimal.
    manual_end_id: last.manual_end_id,
    segment_type: type,
    segment_detail: detail,
    start_distance_m: first.start_distance_m,
    end_distance_m: last.end_distance_m,
    length_m: last.end_distance_m - first.start_distance_m,
    elevation_gain_m: Math.round(elevationGainM * 10) / 10,
    elevation_loss_m: Math.round(elevationLossM * 10) / 10,
    elapsed_s: elapsedS,
    elapsed_start_s: elapsedStartS,
    cutoff: last.cutoff,
    duration_s: durationS && durationS > 0 ? durationS : null,
    moving_pace: paceDen > 0 ? paceNum / paceDen : null,
    moving_gap: gapDen > 0 ? gapNum / gapDen : null,
    moving_asc_vertical_speed: ascDen > 0 ? ascNum / ascDen : null,
    moving_desc_vertical_speed: descDen > 0 ? descNum / descDen : null,
    stopping_time_s: null,
    services: null,
  };
}

export function buildMinimalRacePlan(detailedSegments) {
  if (!detailedSegments || detailedSegments.length === 0) return [];

  const result = [];
  let bucket = [];

  const flushBucket = () => {
    if (bucket.length === 0) return;
    result.push(mergeRouteSegments(bucket));
    bucket = [];
  };

  for (const seg of detailedSegments) {
    if (seg.segment_type === 'checkpoint') {
      flushBucket();
      result.push(seg);
    } else {
      bucket.push(seg);
      // A checkpoint the runner placed themselves ends a leg too — Minimal means
      // "the points I am racing between", and they said this is one of them.
      if (seg.manual_end_id) flushBucket();
    }
  }
  flushBucket();

  result.forEach((seg, idx) => { seg.index = idx; });
  return result;
}
